import { keyframes } from '@emotion/core'
import styled, { CreateStyled } from '@emotion/styled'
import parser from 'ua-parser-js'
import { hexToRGB, luminance, updateHexLuminance } from '../lib/color'
import { getQueryParamValue } from '../lib/uri'

export const FONT_SIZES = [
  { label: 'Default', size: '16px', value: 0 },
  { label: 'Bigger', size: '19px', value: 1 },
  { label: 'Biggest', size: '22px', value: 2 },
]

export const getFontSize = () => {
  const htmlTag = document.querySelector('html')
  if (!htmlTag) {
    return FONT_SIZES[0]
  }
  const fontSize = htmlTag.style.fontSize
  return FONT_SIZES.find(s => s.size === fontSize) || FONT_SIZES[0]
}

export const NAV_HEIGHT = 50
export const FOOTER_HEIGHT = 85
export const MOBILE_FOOTER_HEIGHT = 100
export const SIDEBAR_WIDTH = 300
export const ENCOUNTER_MANAGEMENT_PANEL_WIDTH = 360
export const PATIENT_INFO_PANEL_WIDTH = 360

export const globalStyles: any = theme => ({
  '*, *::before, *::after': {
    boxSizing: 'border-box',
  },
  a: {
    color: 'inherit',
    textDecoration: 'none',
  },
  body: {
    backgroundColor: theme.background,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    margin: 0,
    MozOsxFontSmoothing: 'grayscale',
    overflow: 'hidden',
    padding: 0,
    position: 'relative',
    textRendering: 'optimizeLegibility',
    WebkitFontSmoothing: 'antialiased',
  },
  button: {
    cursor: 'pointer',
  },
  'button, input, select, textarea': {
    background: 'transparent',
    border: 0,
    borderRadius: 0,
    color: 'inherit',
    display: 'inline-block',
    font: 'inherit',
    margin: 0,
    outline: 0,
    padding: 0,
    position: 'relative',
    textTransform: 'none',
    verticalAlign: 'middle',
    WebkitAppearance: 'button',
  },
  'button[disabled]': {
    cursor: 'auto',
  },
  'h1, h2, h3, h4, h5, h6': {
    lineHeight: 1.5,
    margin: 0,
  },
  html: {
    font: 'normal 400 100% / 1.5 -apple-system, sans-serif',
    fontSize: '100%',
    msOverflowStyle: '-ms-autohiding-scrollbar',
    msTextSizeAdjust: '100%',
    overflow: 'hidden',
    WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
    WebkitTextSizeAdjust: '100%',
  },
  img: {
    border: 0,
    fontSize: '0.75em',
    outline: 0,
  },
  'input::-moz-focus-inner, button::-moz-focus-inner': {
    border: 0,
    padding: 0,
  },
  p: {
    margin: 0,
  },
})

export interface Theme {
  accent: {
    basil: string
    crimson: string
    dust: string
    mint: string
  }
  background: string
  border: string
  borderRadius: number
  borderRadiusSmall: number
  ease: string
  error: string
  errorDark: string
  fontFamily: string
  grey: string
  greyDark: string
  greyLight: string
  notificationBg: string
  padding: number
  paddingMobile: number
  primary: string
  primaryDark: string
  primaryLight: string
  primaryLuminance: number
  speed: string
  success: string
  successDark: string
  text: string
  textLight: string
  warning: string
}

// TODO: name properties to be associated with the types of elements they style
// all colors within the site should be applied through this base theme
const base = {
  // accent colors are only for CMD branding at this time
  accent: {
    basil: '#23c6a7',
    crimson: '#f73905',
    dust: '#d5d5d5',
    mint: '#a1dd5e',
  },
  background: '#fff',
  border: '#dcdbdb',
  borderRadius: 14,
  borderRadiusSmall: 4,
  ease: 'ease',
  error: '#fe3824',
  errorDark: updateHexLuminance('#fe3824', -0.5),
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  grey: '#666',
  greyDark: '#2c343c',
  greyLight: '#f8f8f8',
  greyMediumLight: '#b3b3b3',
  notificationBg: '#fe3824',
  padding: 24,
  paddingMobile: 14,
  primary: '#666',
  primaryDark: updateHexLuminance('#666', -0.5),
  primaryLight: updateHexLuminance('#666', 0.3),
  primaryLuminance: luminance(hexToRGB('#666')),
  queueAlert: '#27343D',
  queueStatus: '#004F78',
  queueAssigned: '#268016',
  speed: '0.333s',
  success: '#44db5e',
  successDark: updateHexLuminance('#44db5e', -0.5),
  text: '#1a1a1a',
  textLight: '#fff',
  warning: '#f5c254',
}

export const themeQuery = getQueryParamValue('theme')
export const primaryColor = (window as any).CMD.config.tenant.primary_color

export const patientSettingsVisible = (): boolean =>
  (window as any).CMD.config.tenant.is_patient_settings_visible

export const isSettingsAllowed = (): boolean =>
  (window as any).CMD.config.tenant.is_settings_editable

export const tosLink = (): string =>
  (window as any).CMD.config.tenant.terms_of_service

export const getCurrentRootFontSize = (defaultRoot = 16) => {
  const htmlTag = document.querySelector('html')
  if (!htmlTag) {
    return defaultRoot
  }
  const styleAttr = htmlTag.getAttribute('style')
  if (!styleAttr) {
    return defaultRoot
  }
  const match = styleAttr.match(/(\d+)/)
  if (!match || !match.length) {
    return defaultRoot
  }
  return Number(match[0])
}

// luminance describes the amount of light that is emitted from the screen for
// a particular color and in this case is a float between 0 and 1. we use this
// to change styling based on how light/dark a color is. a value that is less
// than 0.1 is almost black and therefore doesn't work with darker rollover or
// active states for things. see StreamButton for implementation.
export const themeNormal = (
  hex: string = themeQuery || primaryColor,
): Theme => {
  const primary = hex.indexOf('#') === 0 ? hex : `#${hex}`
  const primaryLuminance = luminance(hexToRGB(primary))
  return {
    ...base,
    primary,
    primaryDark: updateHexLuminance(primary, -0.5),
    primaryLight: updateHexLuminance(primary, 0.3),
    primaryLuminance,
  }
}

export const themeHighContrast = (
  hex: string = themeQuery || primaryColor,
): Theme => {
  const primary = updateHexLuminance(
    hex.indexOf('#') === 0 ? hex : `#${hex}`,
    -0.5,
  )
  const primaryLuminance = luminance(hexToRGB(primary))
  const error = updateHexLuminance(base.error, -0.5)
  const success = updateHexLuminance(base.success, -0.5)
  return {
    ...base,
    border: base.text,
    error,
    errorDark: updateHexLuminance(error, -0.5),
    primary,
    primaryDark: updateHexLuminance(primary, -0.5),
    primaryLight: updateHexLuminance(primary, 0.3),
    primaryLuminance,
    success,
    successDark: updateHexLuminance(success, -0.5),
  }
}
// @ts-ignore
const p = parser()
const forceMobileLayout = p && p.device && p.device.type === 'mobile'

export enum MQ {
  DESKTOP = '@media (min-width: 48em)', // 768 / 16 = 48em
  LARGE_DESKTOP = '@media (min-width: 68.75em)', // 1100 / 16 = 68.75em
  TABLET = '@media (min-width: 40em)', // 640 / 16 = 40em
  X_LARGE_DESKTOP = '@media (min-width: 93.75em)', // 1500 / 16 = 68.75em
  IPAD = '@media only screen and (min-device-width: 48em) and (max-device-width: 64em) and (-webkit-min-device-pixel-ratio: 1)',
  IPAD_LANDSCAPE = '@media only screen and (min-device-width: 48em) and (max-device-width: 64em) and (-webkit-min-device-pixel-ratio: 1) and (orientation: landscape)',
  IPAD_PORTRAIT = '@media only screen and (min-device-width: 48em) and (max-device-width: 64em) and (-webkit-min-device-pixel-ratio: 1) and (orientation: portrait)',
  IPHONE_X = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 51.3125em) and (-webkit-min-device-pixel-ratio: 3)',
  IPHONE_X_LANDSCAPE = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 51.3125em) and (-webkit-min-device-pixel-ratio: 3) and (orientation: landscape)',
  IPHONE_X_PORTRAIT = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 51.3125em) and (-webkit-min-device-pixel-ratio: 3) and (orientation: portrait)',
  IPHONE_5_SE = '@media only screen and (min-device-width: 20em) and (max-device-width: 35.5em) and (-webkit-min-device-pixel-ratio: 2)',
  IPHONE_5_SE_LANDSCAPE = '@media only screen and (min-device-width: 20em) and (max-device-width: 35.5em) and (-webkit-min-device-pixel-ratio: 2) and (orientation: landscape)',
  IPHONE_5_SE_PORTRAIT = '@media only screen and (min-device-width: 20em) and (max-device-width: 35.5em) and (-webkit-min-device-pixel-ratio: 2) and (orientation: portrait)',
  IPHONE_6_7_8 = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 41.6875em) and (-webkit-min-device-pixel-ratio: 2)',
  IPHONE_6_7_8_LANDSCAPE = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 41.6875em) and (-webkit-min-device-pixel-ratio: 2) and (orientation: landscape)',
  IPHONE_6_7_8_PORTRAIT = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 41.6875em) and (-webkit-min-device-pixel-ratio: 2) and (orientation: portrait)',
  IPHONE_6_7_8_PLUS = '@media only screen and (min-device-width: 25.875em) and (max-device-width: 46em) and (-webkit-min-device-pixel-ratio: 3)',
  IPHONE_6_7_8_PLUS_LANDSCAPE = '@media only screen and (min-device-width: 25.875em) and (max-device-width: 46em) and (-webkit-min-device-pixel-ratio: 3) and (orientation: landscape)',
  IPHONE_6_7_8_PLUS_PORTRAIT = '@media only screen and (min-device-width: 25.875em) and (max-device-width: 46em) and (-webkit-min-device-pixel-ratio: 3) and (orientation: portrait)',
}

export const addFontSizeStyles = (
  obj,
  content,
  mq,
  fontSize,
  setValue = true,
) => {
  if (!obj[mq]) {
    obj[mq] = {}
  }
  Object.keys(content).forEach(rule => {
    const value = content[rule]
    const ruleArr = rule.split(', ')
    ruleArr.forEach(r => {
      if (typeof value === 'object') {
        if (r.indexOf('&') > -1) {
          obj[mq][`html[style*="${fontSize}"] ${r}`] = setValue ? value : null
        } else {
          obj[mq][`html[style*="${fontSize}"] & ${r}`] = setValue ? value : null
        }
      } else {
        if (!obj[mq][`html[style*="${fontSize}"] &`]) {
          obj[mq][`html[style*="${fontSize}"] &`] = {}
        }
        obj[mq][`html[style*="${fontSize}"] &`][r] = setValue ? value : null
      }
    })
  })
  return obj
}

export const MQ_TABLET = (content: any): any => {
  const obj = {}
  FONT_SIZES.forEach((fontSize, index) => {
    if (index > 0) {
      addFontSizeStyles(obj, content, MQ.DESKTOP, fontSize.size)
      addFontSizeStyles(obj, content, MQ.TABLET, fontSize.size, false)
    } else {
      addFontSizeStyles(
        obj,
        content,
        MQ.TABLET,
        fontSize.size,
        !forceMobileLayout,
      )
    }
  })
  return obj
}

export const ellipsis: any = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export const hitArea: any = {
  bottom: 0,
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
}

export const boldRolloverFix: any = {
  content: 'attr(title)',
  display: 'block',
  fontWeight: 'bold',
  height: 0,
  overflow: 'hidden',
  visibility: 'hidden',
}

export const focusNoneStyle: any = {
  '&:focus': {
    border: 'none',
    outline: 'none',
  },
}

// previously in FormElementParts.tsx, importing stopped working
export const elementProps: any = {
  display: 'block',
  fontSize: 16,
  padding: '0 30px 10px',
  width: '100%',
}

// previously in FormElementParts.tsx, importing stopped working
export const labelWhenSmallProps: any = {
  '[icon] &': {
    left: -30,
  },
  fontSize: 13,
  fontWeight: 'bold',
  left: 0,
  top: -22,
}

export const isPatient = () => (window as any).CMD.user.type === 'Patient'
export const isProvider = () => (window as any).CMD.user.type === 'Doctor'

export const pulse = (hex, size) => {
  const rgb = hexToRGB(hex)
  return keyframes({
    '0%': { boxShadow: `0 0 0 0 rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.8)` },
    '70%': {
      boxShadow: `0 0 0 ${size / 3}px rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0)`,
    },
    '100%': { boxShadow: `0 0 0 0 rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0)` },
  })
}
export const spin = keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '100%': { transform: 'rotate(360deg)' },
})

export default styled as CreateStyled<Theme>
